/**
 * The P0-E routing controls (INC-0807; U041, U042, U044, U045, U047-U049), enabled only behind
 * `capabilities.routing_enabled`: block entry, modify, cancel one and all, take over, resume after
 * the cooldown with a written review and an out-of-band code, and raise a risk limit. Every one is
 * a launcher into the shared command dialog; nothing here sends a command itself.
 */
import {
  ROUTING_FACTS,
  ContractViolation,
  decodeBalanceRow,
  decodeControlRow,
  decodeLimitRow,
  decodeOrderRow,
  decodeRoutingFacts,
  type ControlRow,
  type LimitRow,
  type OrderRow,
  type RoutingFacts,
} from "../../contracts/index.ts";
import type { Route } from "../../app/router.ts";
import { INTENTS, codeGuardOf } from "../../app/command-dialog.ts";
import type { Mode } from "../../contracts/envelope.ts";
import { badge, definitions, instantCell, renderFreshnessBar } from "../../app/components.ts";
import { h, setChildren } from "../../app/dom.ts";
import { formatPaise } from "../../app/format.ts";
import { renderStateBanner, stateOfEnvelope, stateOfError } from "../../app/state.ts";
import { anchor } from "../../app/time.ts";
import type { ViewContext, ViewFactory, ViewHandle } from "../../app/view.ts";
import { controlOf } from "../risk/model.ts";
import { isCancellable, quantityText } from "../orders/model.ts";
import { CommandSurface } from "../command-surface.ts";
import { handleOf, makeFrame } from "../frame.ts";
import { cooldownEnd, isModifiable, modificationUse, raisableLimits } from "./model.ts";

type Clock = ReturnType<typeof anchor>;

const G5 = "Not enabled before G5: broker routing is off, so this action does not exist yet.";

/** The seven P0-E items, in page order: the id, the heading and what it is. */
const ITEMS: readonly (readonly [string, string, string])[] = [
  ["U041", "Block order entry", "block"],
  ["U042", "Modify an order", "modify"],
  ["U044", "Cancel one order or all orders", "cancel"],
  ["U045", "Manual takeover", "takeover"],
  ["U047", "Resume after the cooldown", "resume"],
  ["U048", "Raise a risk limit", "risk"],
  ["U049", "Out-of-band confirmation", "confirm"],
];

/** `#/orders/routing` is this page; any other orders route is the orders view (`null`). */
export function resolveRouting(route: Route): ViewFactory | null {
  return route.sub === "routing" ? (ctx) => mountRouting(ctx) : null;
}

function section(id: string, title: string, key: string, ...body: (HTMLElement | null)[]) {
  return h(
    "section",
    { attrs: { "aria-labelledby": `routing-${key}`, "data-routing": key } },
    h("h2", { attrs: { id: `routing-${key}` } }, `${id} ${title}`),
    ...body,
  );
}

interface Loaded {
  readonly orders: readonly OrderRow[];
  readonly controls: readonly ControlRow[];
  readonly limits: readonly LimitRow[];
  readonly facts: RoutingFacts;
  /** The facts envelope's mode: with no live code, ASSISTED and LIVE still need one for a risk-increasing command. */
  readonly mode: Mode | null;
}

function blockSection(surface: CommandSurface, data: Loaded, accountId: string): HTMLElement {
  const entries = controlOf(data.controls, "ENTRIES");
  const enabled = entries?.state === "ENABLED";
  return section(
    "U041",
    "Block order entry",
    "block",
    h(
      "p",
      { class: "note" },
      "An authorised operator blocks new entries at once. This needs no second-factor round trip; it is still authenticated and audited. It closes nothing and cancels nothing.",
    ),
    h(
      "div",
      { class: "actions" },
      surface.launcher({
        intent: "pause-entries",
        scope: `account ${accountId}`,
        quantities: "new entries only; open positions and working orders are not touched",
        payload: { scope: "ACCOUNT" },
        applicable: enabled,
        inapplicable: enabled ? undefined : "New entries are already not enabled.",
      }),
    ),
  );
}

function modifySection(surface: CommandSurface, data: Loaded, clock: Clock) {
  const live = data.orders.filter(isModifiable);
  return section(
    "U042",
    "Modify an order",
    "modify",
    h(
      "p",
      { class: "note" },
      `The broker allows at most ${String(data.facts.modificationCap)} modifications of one order. The count below is the server's.`,
    ),
    live.length === 0
      ? h("p", {}, "No order can be modified now.")
      : h(
          "ul",
          { class: "modifiable" },
          ...live.map((row) => {
            const use = modificationUse(data.facts, row.orderId);
            return h(
              "li",
              { attrs: { "data-modify": row.orderId, "data-capped": String(use.capped) } },
              h("strong", {}, `${row.orderId} ${row.instrument}`),
              ` ${quantityText(row)}; `,
              row.limitPaise === null ? "market" : `limit ${formatPaise(row.limitPaise)}`,
              "; ",
              badge(
                `${String(use.used)} of ${String(use.cap)} modifications used`,
                use.capped ? "bad" : "info",
              ),
              " ",
              surface.launcher({
                intent: "modify-order",
                scope: `order ${row.orderId} (${row.instrument})`,
                quantities: `${quantityText(row)}; ${String(use.used)} of ${String(use.cap)} modifications used`,
                payload: { order_id: row.orderId },
                routing: {
                  clock,
                  modification: { used: use.used, cap: use.cap },
                  ...codeGuardOf("modify-order", data.facts, data.mode),
                },
                applicable: !use.capped,
                inapplicable: use.capped ? "The modification cap is reached." : undefined,
              }),
            );
          }),
        ),
  );
}

function cancelSection(surface: CommandSurface, data: Loaded, accountId: string): HTMLElement {
  const live = data.orders.filter(isCancellable);
  return section(
    "U044",
    "Cancel one order or all orders",
    "cancel",
    h(
      "p",
      { class: "note" },
      "Cancel order and Cancel all orders are separate commands with separate confirmations. Cancelling a protective stop removes the protection of its position.",
    ),
    live.length === 0
      ? h("p", {}, "No working order to cancel.")
      : h(
          "ul",
          { class: "cancellable" },
          ...live.map((row) =>
            h(
              "li",
              { attrs: { "data-cancel": row.orderId } },
              h("strong", {}, `${row.orderId} ${row.instrument}`),
              ` ${row.role.replace("_", " ").toLowerCase()}, ${row.state}; `,
              surface.launcher({
                intent: "cancel-order",
                scope: `order ${row.orderId} (${row.instrument})`,
                quantities: quantityText(row),
                payload: { order_id: row.orderId },
              }),
            ),
          ),
        ),
    h(
      "p",
      { class: "warning" },
      "Cancel all orders cancels every working order, protective stops included, and needs its own authority.",
    ),
    h(
      "div",
      { class: "actions" },
      surface.launcher({
        intent: "cancel-all-orders",
        scope: `account ${accountId}`,
        quantities: "every working order in the account, protective stops included",
        payload: { scope: "ACCOUNT" },
      }),
    ),
  );
}

/** A takeover is risk-increasing: a code in ASSISTED and LIVE, nothing else to check. */
function takeoverGuard(data: Loaded, clock: Clock) {
  const code = codeGuardOf("manual-takeover", data.facts, data.mode);
  return Object.keys(code).length === 0 ? {} : { routing: { clock, ...code } };
}

function takeoverSection(surface: CommandSurface, data: Loaded, accountId: string, clock: Clock) {
  const owner = controlOf(data.controls, "EXECUTION_OWNER");
  const controller = owner?.state === "ENABLED";
  return section(
    "U045",
    "Manual takeover",
    "takeover",
    definitions([
      [
        "Execution owner",
        owner === undefined
          ? "unknown"
          : badge(owner.state === "MANUAL" ? "OPERATOR" : "CONTROLLER", "info"),
      ],
    ]),
    h(
      "p",
      { class: "note" },
      "Taking over needs a fresh second factor. Logging out or closing the tab never hands ownership back.",
    ),
    h(
      "div",
      { class: "actions" },
      surface.launcher({
        intent: "manual-takeover",
        scope: `account ${accountId}`,
        quantities: "execution ownership only; positions and orders are unchanged",
        payload: { scope: "ACCOUNT" },
        ...takeoverGuard(data, clock),
        applicable: controller,
        inapplicable: controller ? undefined : "The controller does not own execution now.",
      }),
    ),
  );
}

function resumeSection(surface: CommandSurface, data: Loaded, accountId: string, clock: Clock) {
  const { halt } = data.facts;
  const paused = controlOf(data.controls, "ENTRIES")?.state === "PAUSED";
  const ready = halt !== null && paused;
  return section(
    "U047",
    "Resume after the cooldown",
    "resume",
    halt === null
      ? h("p", {}, "The account is not halted: there is nothing to resume.")
      : definitions([
          ["Halted since", instantCell(halt.haltedSince)],
          ["Cooldown the server applies", `${String(halt.cooldownSeconds / 60)} minutes`],
          ["Cooldown ends", instantCell(cooldownEnd(halt))],
        ]),
    h(
      "p",
      { class: "note" },
      "Resume is refused before the cooldown has elapsed on the server's clock and without a written review and the out-of-band code. The dialog shows the time left.",
    ),
    h(
      "div",
      { class: "actions" },
      surface.launcher({
        intent: "resume",
        scope: `account ${accountId}`,
        quantities: "new entries are paused; open positions are unchanged",
        payload: { scope: "ACCOUNT" },
        routing: {
          clock,
          reviewRequired: halt?.reviewRequired ?? true,
          ...(halt === null ? {} : { cooldown: { endsAt: cooldownEnd(halt) } }),
          ...codeGuardOf("resume", data.facts, data.mode),
        },
        applicable: ready,
        inapplicable: ready ? undefined : "Entries are not halted.",
      }),
    ),
  );
}

function riskSection(surface: CommandSurface, data: Loaded, clock: Clock) {
  const limits = raisableLimits(data.limits);
  return section(
    "U048",
    "Raise a risk limit",
    "risk",
    h(
      "p",
      { class: "note" },
      "Raising a limit takes a fresh second factor. Lowering one is not offered here: it is never held back.",
    ),
    h(
      "ul",
      { class: "raisable" },
      ...limits.map((limit) =>
        h(
          "li",
          { attrs: { "data-limit": limit.limitId } },
          h("strong", {}, `${limit.label} `),
          `limit ${formatPaise(limit.limitPaise)} (policy ${limit.policyVersion}). `,
          surface.launcher({
            intent: "increase-risk",
            scope: `limit ${limit.limitId}`,
            quantities: `current limit ${formatPaise(limit.limitPaise)} under policy ${limit.policyVersion}`,
            payload: { direction: "INCREASE", limit_id: limit.limitId },
            routing: {
              clock,
              amountAbovePaise: limit.limitPaise,
              ...codeGuardOf("increase-risk", data.facts, data.mode),
            },
          }),
        ),
      ),
    ),
  );
}

/** The label of the intent whose server action this is, else the action itself (item 168). */
function actionLabel(action: string): string {
  return Object.values(INTENTS).find((spec) => spec.action === action)?.label ?? action;
}

function confirmSection(data: Loaded) {
  const { outOfBand } = data.facts;
  return section(
    "U049",
    "Out-of-band confirmation",
    "confirm",
    outOfBand === null
      ? h(
          "p",
          {},
          "No confirmation is pending: you have no live code. A risk-increasing command in ASSISTED or LIVE mode asks for one in its dialog.",
        )
      : definitions([
          ["For", actionLabel(outOfBand.action)],
          ["Sent by", outOfBand.channel],
          ["Issued", instantCell(outOfBand.issuedAt)],
          ["Expires", instantCell(outOfBand.expiresAt)],
        ]),
    h(
      "p",
      { class: "note" },
      "The server sends a code through another channel when you ask for one in the dialog of a risk-increasing command (ASSISTED and LIVE). Type it by hand there. It is never shown here, stored, put in a link or kept after the command is sent.",
    ),
  );
}

/** Mount the routing page; without broker routing it shows the blocker for every item and reads nothing. */
export function mountRouting(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("routing", "Routing controls");
  if (!ctx.capabilities.routingEnabled) {
    frame.banner.append(
      renderStateBanner({
        kind: "unavailable",
        reason:
          "the routing controls are not enabled before G5 (broker routing is off; the server's capability flag is false)",
      }),
    );
    setChildren(
      frame.body,
      ITEMS.map(([id, title, key]) => section(id, title, key, h("p", { class: "g5" }, G5))),
    );
    return handleOf(frame, ctx);
  }
  if (ctx.accountId === null) {
    frame.banner.append(renderStateBanner({ kind: "no-observations" }));
    return handleOf(frame, ctx);
  }
  const account: string = ctx.accountId;
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  const surface = new CommandSurface(ctx, load);

  async function load(): Promise<void> {
    surface.reset();
    const params = { accountId: account };
    try {
      const [orders, balances, controls, limits, facts] = await Promise.all([
        ctx.client.read("orders", decodeOrderRow, params),
        ctx.client.read("balances", decodeBalanceRow, params),
        ctx.client.read("risk_controls", decodeControlRow, params),
        ctx.client.read("limits", decodeLimitRow, params),
        ctx.client.read(ROUTING_FACTS, decodeRoutingFacts, params),
      ]);
      if (
        orders === null ||
        balances === null ||
        controls === null ||
        limits === null ||
        facts === null ||
        ctx.scope.disposed
      ) {
        return;
      }
      const [row] = facts.page.rows;
      if (facts.accountId !== account || row === undefined || facts.page.rows.length !== 1) {
        throw new ContractViolation("routing_facts", "one row for the selected account");
      }
      if (row.halt !== null && row.halt.haltedSince > facts.snapshotTime) {
        throw new ContractViolation("routing_facts.halt", "a halt that began before the snapshot");
      }
      const state = stateOfEnvelope(orders);
      setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
      setChildren(frame.freshness, [
        renderFreshnessBar(orders, { streamSequence: ctx.streamSequence.get() }),
      ]);
      const data: Loaded = {
        orders: orders.page.rows,
        controls: controls.page.rows,
        limits: limits.page.rows,
        facts: row,
        mode: facts.mode,
      };
      const clock = anchor(facts.snapshotTime, ctx.mono);
      surface.clearLaunchers();
      setChildren(frame.body, [
        surface.status,
        blockSection(surface, data, account),
        modifySection(surface, data, clock),
        cancelSection(surface, data, account),
        takeoverSection(surface, data, account, clock),
        resumeSection(surface, data, account, clock),
        riskSection(surface, data, clock),
        confirmSection(data),
      ]);
      surface.observe("orders", orders);
      surface.observe("risk_controls", controls);
      surface.observe("limits", limits);
      surface.observe("balances", balances);
      // The facts gate the resume, the caps and the code: stale facts disable commands (review F1).
      surface.observe(ROUTING_FACTS, facts);
      surface.identifyFrom(balances);
    } catch (error) {
      setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
    }
  }

  void load();
  return handleOf(frame, ctx);
}
